import {
  QuestEditorApi,
  QuestMetadataWithQuestId,
  PickQuestMetadataWithQuestIdExcludeKeyofQuestMetadataWithQuestIdQuestEditorDataOrQuestEditorDataDefinitions as QuestMetadataWithQuestIdWithoutData,
  QuestNode,
} from "@worldwidewebb/client-quests";
import { Edge, Node } from "reactflow";
import { ulid } from "ulid";
import { axiosInstance, buildQuestClientBasePath } from "..";
import { StartConditionKind } from "../../components/quests/QuestUpdateTargetModal";
import { QuestData, QuestWithId } from "../../models/api/quest";
import { EdgeType } from "../../models/edgeType";
import { NodeType } from "../../models/nodeType";
import { StartConditionType, StartNodeData } from "../../store/quests";

const questEditorApi = new QuestEditorApi(undefined, buildQuestClientBasePath(), axiosInstance);

export async function getQuests(displayName?: string, isArchived?: boolean): Promise<QuestWithId[]> {
  try {
    const { data: quests } = await questEditorApi.getQuestEditorQuests(displayName, undefined, isArchived);

    return quests
      .sort(({ displayName: a }, { displayName: b }) => a.toLowerCase().localeCompare(b.toLowerCase()))
      .map(mapQuestFromApiWithoutData);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function getQuest(questId: string): Promise<QuestWithId> {
  try {
    const { data: quest } = await questEditorApi.getQuestEditorQuest(questId);

    return mapQuestFromApi(quest);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function getQuestOrNull(questId: string | null): Promise<QuestWithId | null> {
  if (questId == null) {
    return null;
  }

  return await getQuest(questId);
}

export async function createQuest(name: string) {
  const quest: QuestWithId = {
    questId: ulid(),
    version: ulid(),
    name,
    description: "",
    data: {
      nodes: [],
      edges: [],
    },
    status: "draft",
    isReady: false,
    isTemplate: false,
    folderId: "",
    authorId: "",
    priority: 0,
    repeatPeriod: "none",
    questNpcId: "",
    visible: false,
    updatedAt: new Date().toISOString(),
  };

  return await setQuestMetaDataAndNodeData(quest, []);
}

export type CreateDuplicates = (
  nodes: Node<NodeType>[],
  edges: Edge<EdgeType>[]
) => Promise<{ nodes: Node<NodeType>[]; edges: Edge<EdgeType>[] }>;

export type ExportQuestNodes = (nodes: Node<NodeType>[], edges: Edge<EdgeType>[], nodeTypes: NodeType[]) => QuestNode[];

export async function duplicateQuest(
  questId: string,
  createDuplicates: CreateDuplicates,
  exportQuestNodes: ExportQuestNodes,
  nodeTypes: NodeType[]
) {
  const quest = await getQuest(questId);

  const { name, data: { nodes, edges } = { nodes: [], edges: [] } } = quest;

  const { nodes: duplicatedNodes, edges: duplicatedEdges } = await createDuplicates(nodes, edges);

  const updatedQuest: QuestWithId = {
    ...quest,
    questId: ulid(),
    version: ulid(),
    name: `${name} (duplicate)`,
    data: {
      nodes: duplicatedNodes,
      edges: duplicatedEdges,
    },
    isReady: false,
    status: "draft",
  };

  const updatedNodes = exportQuestNodes(duplicatedNodes, duplicatedEdges, nodeTypes);

  return await setQuestMetaDataAndNodeData(updatedQuest, updatedNodes);
}

export type DeployEnvironment = "dev" | "stage" | "candidate" | "beta" | "prod-euro" | "prod-euro2" | "prod-asia" | "prod-asia1" | "prod-sam" | "prod-usa2";

// The editor host for each environment. "Deploy to <env>" pushes the quest to that
// host's quest-editor service, which writes it into that cluster's own content DB.
// "beta" is webb-beta, our primary prod project (legacy naming -- see AGENTS.md).
// "candidate" is prod-usa1 (webb-prod-usa1): the pre-live canary that CI deploys to in
// its "candidate (prod-usa1)" stage. It is a standalone target, not a live region, so
// it is deliberately NOT part of "Deploy to all production".
// The remaining prod-* hosts are the live Early-Access regional clusters. prod-euro1,
// prod-euro4 and prod-usa3 are not in use, so they are intentionally omitted. prod-euro3
// was retired (2026-09-08). prod-euro2 and prod-usa2 came online 2026-09-11; prod-asia1 is live too.
export const DEPLOY_ENVIRONMENT_HOSTS: Record<DeployEnvironment, string> = {
  dev: "quest-editor.apps.webb.party",
  stage: "quest-editor.apps.webb.black",
  candidate: "quest-editor.apps.soulbound-usa1.win",
  beta: "quest-editor.apps.soulbound.site",
  "prod-euro": "quest-editor.apps.soulbound.uk",
  "prod-euro2": "quest-editor.apps.soulbound-euro2.win",
  "prod-asia": "quest-editor.apps.soulbound.asia",
  "prod-asia1": "quest-editor.apps.soulbound-asia1.win",
  "prod-sam": "quest-editor.apps.soulbound.cloud",
  "prod-usa2": "quest-editor.apps.soulbound-usa2.win",
};

export const DEPLOY_ENVIRONMENT_LABELS: Record<DeployEnvironment, string> = {
  dev: "DEV",
  stage: "STAGE",
  candidate: "CANDIDATE (prod-usa1)",
  beta: "BETA",
  "prod-euro": "PROD (Euro)",
  "prod-euro2": "PROD (Euro 2)",
  "prod-asia": "PROD (Asia)",
  "prod-asia1": "PROD (Asia 1)",
  "prod-sam": "PROD (SAM)",
  "prod-usa2": "PROD (USA 2)",
};

// The set of production regions targeted by "Deploy to all production".
export const PRODUCTION_ENVIRONMENTS: DeployEnvironment[] = ["beta", "prod-euro", "prod-euro2", "prod-asia", "prod-asia1", "prod-sam", "prod-usa2"];

async function getQuestDeployAuth() {
  try {
    const { data } = await questEditorApi.getQuestDeployAuth();

    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function deployQuest(
  questId: string,
  environment: DeployEnvironment,
  exportQuestNodes: ExportQuestNodes,
  nodeTypes: NodeType[]
) {
  const questClientBasePath = `https://${DEPLOY_ENVIRONMENT_HOSTS[environment]}/api/quest-editor`;

  const questEditorApi = new QuestEditorApi(undefined, questClientBasePath, axiosInstance);

  try {
    const quest = await getQuest(questId);
    const { data } = quest;

    const questData = typeof data === "string" ? JSON.parse(data) : data;

    await questEditorApi.setQuest(
      quest.questId,
      {
        metadata: mapQuestToApi(quest),
        nodes: exportQuestNodes(questData.nodes, questData.edges, nodeTypes),
      },
      {
        auth: await getQuestDeployAuth(),
      }
    );
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export interface DeployResult {
  environment: DeployEnvironment;
  ok: boolean;
  error?: string;
}

// Deploy a quest to several environments, one call per environment. Never rejects:
// each environment's outcome is captured independently so a partial rollout (some
// regions deployed, others failed) is reported back rather than aborting the rest.
export async function deployQuestToEnvironments(
  questId: string,
  environments: DeployEnvironment[],
  exportQuestNodes: ExportQuestNodes,
  nodeTypes: NodeType[]
): Promise<DeployResult[]> {
  return Promise.all(
    environments.map(async (environment): Promise<DeployResult> => {
      try {
        await deployQuest(questId, environment, exportQuestNodes, nodeTypes);

        return { environment, ok: true };
      } catch (error) {
        return { environment, ok: false, error: error instanceof Error ? error.message : String(error) };
      }
    })
  );
}

export async function updateQuestMetaDatas(quests: Pick<QuestWithId, "questId" | "questEditorPosition">[]) {
  try {
    await questEditorApi.updateQuestEditorQuestMetadatas(quests);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function updateQuestMetaData(questId: string, partialQuest: Partial<QuestWithId>) {
  const currentQuest = await getQuest(questId);
  const updatedQuest = { ...currentQuest, ...partialQuest };

  return await setQuestMetaData(updatedQuest);
}

export async function updateQuestMetaDataAndNodeData(
  questId: string,
  partialQuest: Partial<QuestWithId>,
  questData: QuestData,
  nodeTypes: NodeType[],
  exportQuestNodes: ExportQuestNodes
) {
  const currentQuest = await getQuest(questId);
  const partialNodes = { data: questData };
  const updatedQuest = { ...currentQuest, ...partialNodes, ...partialQuest, version: ulid() };

  const updatedQuestNodes = exportQuestNodes(questData.nodes, questData.edges, nodeTypes);

  return await setQuestMetaDataAndNodeData(updatedQuest, updatedQuestNodes);
}

export async function updateQuestWithStartCondition<T>(
  questId: string,
  startConditionNodeId: string,
  startConditionKind: StartConditionKind,
  startConditionType: StartConditionType,
  value: T,
  nodeTypes: NodeType[],
  exportQuestNodes: ExportQuestNodes
) {
  const currentQuest = await getQuest(questId);
  const updatedQuest = { ...currentQuest, version: ulid() };

  const currentNodes = currentQuest.data?.nodes ?? [];
  const currentEdges = currentQuest.data?.edges ?? [];

  const updatedNodes = currentNodes.map((node) => {
    if (node.id !== startConditionNodeId) {
      return node;
    }

    const startConditionNode: Node<NodeType<StartNodeData>> = node;

    startConditionNode.data.nodeData[startConditionKind === "all" ? "allStartConditions" : "anyStartConditions"].push({
      id: ulid(),
      type: startConditionType,
      value,
    });

    return node;
  });

  const updatedEdges = currentEdges;

  const updatedQuestNodes = exportQuestNodes(updatedNodes, updatedEdges, nodeTypes);

  return await setQuestMetaDataAndNodeData(updatedQuest, updatedQuestNodes);
}

async function setQuestMetaData(quest: QuestWithId): Promise<QuestWithId> {
  try {
    const { data } = await questEditorApi.updateQuestEditorQuestMetadata(quest.questId, mapQuestToApi(quest));

    return mapQuestFromApi(data);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function setQuestMetaDataAndNodeData(quest: QuestWithId, questNodes: QuestNode[]): Promise<QuestWithId> {
  try {
    const { data } = await questEditorApi.setQuestEditorQuestMetadataAndNodes(quest.questId, {
      metadata: mapQuestToApi(quest),
      nodes: questNodes,
    });

    return mapQuestFromApi(data);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function deleteQuestById(questId: string): Promise<void> {
  try {
    await questEditorApi.deleteQuestEditorQuest(questId);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function resetQuest(questId: string, userId: string): Promise<void> {
  try {
    await questEditorApi.resetEditorQuestForUser(userId, questId);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function resetQuests(questIds: string[], userId: string): Promise<void> {
  try {
    await questEditorApi.resetSpecifiedQuestsForUser(userId, { questIds });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export function mapQuestFromApiWithoutData(quest: QuestMetadataWithQuestIdWithoutData): QuestWithId {
  return {
    ...quest,
    name: quest.displayName,
    data: undefined,
    folderId: quest.questEditorFolderId,
  };
}

export function mapQuestFromApi(quest: QuestMetadataWithQuestId): QuestWithId {
  return {
    ...quest,
    name: quest.displayName,
    data: typeof quest.questEditorData == "string" ? JSON.parse(quest.questEditorData) : quest.questEditorData,
    folderId: quest.questEditorFolderId,
  };
}

export function mapQuestToApi(quest: QuestWithId): QuestMetadataWithQuestId {
  const { name, data, folderId, ...questFields } = quest;

  return {
    ...questFields,
    displayName: name,
    questEditorData: JSON.stringify(data),
    questEditorDataDefinitions: JSON.stringify([]),
    questEditorFolderId: folderId,
  };
}
